<template>
  <v-row justify="center" class="my-0 py-4">
    <v-col cols="11" class="white rounded-xl ps-page">
      <!-- Header -->
      <v-row class="px-md-4 px-2 pt-4 align-center">
        <v-col cols="12" md="7">
          <h1 class="ps-title">Mis Productos y Servicios</h1>
          <p class="ps-subtitle mb-0">
            Crea, publica y administra los productos y servicios que tu organización ofrece a sus miembros.
          </p>
        </v-col>
        <v-col cols="12" md="5" class="d-flex justify-md-end">
          <v-btn
            color="secondary"
            depressed
            large
            class="rounded-lg font-weight-bold ps-cta"
            :style="{ color: color.primaryVariant }"
            to="/mis-productos/nuevo"
          >
            <v-icon left>mdi-plus</v-icon>
            Crear producto o servicio
          </v-btn>
        </v-col>
      </v-row>

      <!-- Filtros + búsqueda -->
      <v-row class="px-md-4 px-2 align-center" v-if="!emptyState">
        <v-col cols="12" md="6" class="py-2">
          <v-chip-group v-model="statusFilter" mandatory column>
            <v-chip
              v-for="option in statusOptions"
              :key="option.value || 'all'"
              :value="option.value"
              :color="statusFilter === option.value ? 'secondary' : ''"
              :text-color="statusFilter === option.value ? '#1b273e' : 'primaryVariant'"
              :outlined="statusFilter !== option.value"
              filter
              class="font-weight-bold"
            >
              {{ option.label }}
            </v-chip>
          </v-chip-group>
        </v-col>
        <v-col cols="12" md="6" class="py-2">
          <v-text-field
            v-model="search"
            solo
            flat
            dense
            hide-details
            background-color="#f5f6f8"
            class="rounded-lg"
            placeholder="Buscar por nombre"
            prepend-inner-icon="mdi-magnify"
            clearable
            @input="debouncedFetch"
          />
        </v-col>
      </v-row>

      <!-- Loader -->
      <v-row v-if="loading" justify="center" class="py-12">
        <v-progress-circular indeterminate :color="color.primaryVariant" size="48" />
      </v-row>

      <!-- Empty state -->
      <v-row v-else-if="emptyState" justify="center" class="py-12 px-4">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="120" :color="color.primaryVariant" class="mb-4">
            mdi-storefront-outline
          </v-icon>
          <h2 class="ps-empty-title">Aún no has creado productos</h2>
          <p class="ps-empty-text mt-3 mb-6">
            Configura tu primer producto para ofrecerlo a tus miembros y registrar ventas con facturación incluida.
          </p>
          <v-btn
            color="secondary"
            depressed
            large
            class="rounded-lg font-weight-bold ps-cta"
            :style="{ color: color.primaryVariant }"
            to="/mis-productos/nuevo"
          >
            <v-icon left>mdi-plus</v-icon>
            Crear producto
          </v-btn>
        </v-col>
      </v-row>

      <!-- Grid -->
      <v-row v-else class="px-md-4 px-2 pb-6">
        <v-col
          v-for="ps in filteredList"
          :key="ps._id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card class="ps-card rounded-xl" elevation="2" @click="goToDetail(ps._id)">
            <div class="ps-card__banner">
              <v-img
                :src="ps.bannerImage || placeholderBanner"
                :aspect-ratio="16 / 9"
                cover
              />
              <v-chip
                :color="statusColor(ps.status)"
                text-color="white"
                small
                class="ps-card__status font-weight-bold"
              >
                {{ statusLabel(ps.status) }}
              </v-chip>
            </div>
            <v-card-text class="pa-4">
              <div class="ps-card__name">{{ ps.name }}</div>
              <div class="ps-card__price">
                {{ formatPrice(ps.price, ps.currency) }}
              </div>
              <div class="ps-card__meta mt-2 d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon small color="grey">mdi-tag-outline</v-icon>
                  <span class="ml-1">{{ ps.salesCount || 0 }} ventas</span>
                </div>
                <v-menu bottom offset-y @click.native.stop>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                      <v-icon size="18">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="pa-2">
                    <v-list class="pa-0" dense>
                      <v-list-item class="pa-0">
                        <v-list-item-content>
                          <v-btn
                            text
                            elevation="0"
                            class="text-capitalize"
                            v-ripple="false"
                            v-clipboard:copy="shareMicrositeLink(ps._id)"
                            v-clipboard:success="onCopy"
                            v-clipboard:error="onError"
                          >
                            <v-icon size="18" class="pr-2">fas fa-link</v-icon>
                            <span>Copiar url</span>
                          </v-btn>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import { colors } from "@/constants/colors";

export default {
  name: "MyProductsServices",
  data() {
    return {
      color: colors,
      loading: false,
      search: "",
      statusFilter: null,
      statusOptions: [
        { value: null, label: "Todos" },
        { value: "DRAFT", label: "Borrador" },
        { value: "PUBLISHED", label: "Publicado" },
        { value: "PAUSED", label: "Pausado" },
        { value: "ARCHIVED", label: "Archivado" },
      ],
      placeholderBanner:
        "https://wechamber-public.s3.amazonaws.com/placeholders/product-service-placeholder.png",
      debounceTimer: null,
    };
  },
  computed: {
    ...mapState("productServices", ["productsServicesList"]),
    listDocs() {
      return this.productsServicesList || [];
    },
    emptyState() {
      return !this.loading && this.listDocs.length === 0 && !this.search && !this.statusFilter;
    },
    filteredList() {
      return this.listDocs;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    ...mapActions("productServices", ["fetchProductsServices"]),
    ...mapMutations("notification", ["show"]),
    async fetch() {
      this.loading = true;
      const params = { limit: 50 };
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.search) params.search = this.search;
      const res = await this.fetchProductsServices(params);
      this.loading = false;
      if (!res.status) {
        this.show({ text: res.message || "Error al cargar productos.", color: "error" });
      }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(this.fetch, 300);
    },
    goToDetail(id) {
      this.$router.push(`/mi-producto-servicio/${id}`).catch(() => {});
    },
    shareMicrositeLink(id) {
      return `${window.location.origin}/ps/${id}`;
    },
    onCopy() {
      this.show({ text: 'URL copiada al portapapeles', color: 'success' });
    },
    onError() {
      this.show({ text: 'No se pudo copiar la URL', color: 'error' });
    },
    formatPrice(value, currency) {
      const amount = Number(value || 0).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${amount} ${currency || "MXN"}`;
    },
    statusLabel(status) {
      const map = {
        DRAFT: "Borrador",
        PUBLISHED: "Publicado",
        PAUSED: "Pausado",
        ARCHIVED: "Archivado",
      };
      return map[status] || status;
    },
    statusColor(status) {
      const map = {
        DRAFT: "grey",
        PUBLISHED: "success",
        PAUSED: "warning",
        ARCHIVED: "blue-grey",
      };
      return map[status] || "grey";
    },
  },
  watch: {
    statusFilter() {
      this.fetch();
    },
  },
};
</script>

<style scoped>
.ps-page {
  min-height: 70vh;
}
.ps-title {
  color: #1b273e;
  font-weight: 700;
  font-size: 28px;
  letter-spacing: -0.5px;
}
.ps-subtitle {
  color: #5a6478;
  font-size: 14px;
  max-width: 540px;
}
.ps-cta {
  text-transform: none;
  letter-spacing: 0;
}
.ps-empty-title {
  color: #1b273e;
  font-weight: 700;
  font-size: 22px;
}
.ps-empty-text {
  color: #5a6478;
  font-size: 15px;
  max-width: 540px;
  margin: 0 auto;
}
.ps-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}
.ps-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(27, 39, 62, 0.12) !important;
}
.ps-card__banner {
  position: relative;
}
.ps-card__status {
  position: absolute;
  top: 12px;
  left: 12px;
}
.ps-card__name {
  color: #1b273e;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
  min-height: 42px;
}
.ps-card__price {
  color: #1b273e;
  font-weight: 700;
  font-size: 20px;
  margin-top: 8px;
}
.ps-card__meta {
  color: #7a8294;
  font-size: 13px;
  display: flex;
  align-items: center;
}
</style>
